import React, { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    AccountBalance as AccountBalanceIcon,
    Place as PlaceIcon,
    Public as PublicIcon,
    Send as SendIcon,
    Smartphone as SmartphoneIcon,
    Verified as VerifiedIcon,
    Wallet as WalletIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useWalletSafe } from '../contexts/WalletContext'

const diasporaCountryCodes = ['US', 'CA', 'GB', 'AU', 'FR', 'EU']
const senderCurrencyByCountry = { US: 'USD', CA: 'CAD', GB: 'GBP', AU: 'AUD', FR: 'EUR', EU: 'EUR', SL: 'SLE', LR: 'USD', GN: 'GNF', GM: 'GMD' }
const usdToCurrency = { USD: 1, CAD: 1.35, GBP: 0.78, AUD: 1.52, EUR: 0.92, SLE: 21, GNF: 8600, GMD: 69 }
const deliveryOptions = [
    { value: 'wallet', label: 'MojoPayr Wallet', icon: <WalletIcon /> },
    { value: 'mobile', label: 'Mobile Money', icon: <SmartphoneIcon /> },
    { value: 'bank', label: 'Bank Account', icon: <AccountBalanceIcon /> },
]
const sendCountries = {
    SL: { code: 'SL', name: 'Sierra Leone', receiveCurrency: 'SLE', senderRateFromUsd: 21, dialCode: '+232', localLeadingZero: '0', minDigits: 8, maxDigits: 8, allowedPrefixes: ['76', '77', '78', '79', '30', '31', '32', '33'] },
    LR: { code: 'LR', name: 'Liberia', receiveCurrency: 'USD', senderRateFromUsd: 1, dialCode: '+231', localLeadingZero: '0', minDigits: 8, maxDigits: 8, allowedPrefixes: ['77', '88'] },
    GN: { code: 'GN', name: 'Guinea', receiveCurrency: 'GNF', senderRateFromUsd: 8600, dialCode: '+224', localLeadingZero: '', minDigits: 9, maxDigits: 9, allowedPrefixes: ['62', '65', '66'] },
    GM: { code: 'GM', name: 'Gambia', receiveCurrency: 'GMD', senderRateFromUsd: 69, dialCode: '+220', localLeadingZero: '', minDigits: 7, maxDigits: 7, allowedPrefixes: ['2', '3', '7', '9'] },
}
const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 3,
        backgroundColor: '#22154A',
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
        '&.Mui-focused fieldset': { borderColor: '#FF8A00' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
}

const normalizeDigits = (value) => (value || '').replace(/\D/g, '')
const getSenderCurrency = (user) => user?.baseCurrency || senderCurrencyByCountry[String(user?.country || 'US').toUpperCase()] || 'USD'
const getExchangeRate = (fromCurrency, toCurrency) => {
    const from = String(fromCurrency || 'USD').toUpperCase()
    const to = String(toCurrency || 'USD').toUpperCase()
    if (from === to) return 1
    return (usdToCurrency[to] || 1) / (usdToCurrency[from] || 1)
}
const formatMoney = (amount, currency = 'USD') => {
    const numeric = Number(amount || 0)
    const resolvedCurrency = String(currency || 'USD').toUpperCase()
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: resolvedCurrency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numeric)
    } catch (error) {
        return `${resolvedCurrency} ${numeric.toFixed(2)}`
    }
}
const buildLocalPhone = (input, rule) => {
    if (!rule) return ''
    let digits = normalizeDigits(input)
    const internationalDigits = rule.dialCode.replace('+', '')
    if (digits.startsWith(internationalDigits)) digits = digits.slice(internationalDigits.length)
    if (rule.localLeadingZero && digits.startsWith(rule.localLeadingZero)) digits = digits.slice(rule.localLeadingZero.length)
    return digits
}
const validatePhone = (input, rule) => {
    if (!rule) return 'Choose a receiving country first.'
    const localDigits = buildLocalPhone(input, rule)
    if (!localDigits) return 'Enter the mobile number.'
    if (localDigits.length < rule.minDigits || localDigits.length > rule.maxDigits) return `Use a full valid ${rule.name} number.`
    if (!rule.allowedPrefixes.some((prefix) => localDigits.startsWith(prefix))) return `This number does not match supported ${rule.name} mobile prefixes.`
    return ''
}
const firstValidationMessage = (error) => {
    const errors = error?.response?.data?.errors
    if (!errors || typeof errors !== 'object') return error?.response?.data?.message || 'Unable to complete the transfer.'
    const first = Object.values(errors)[0]
    return Array.isArray(first) && first[0] ? first[0] : error?.response?.data?.message || 'Unable to complete the transfer.'
}
const sendText = (isFr) => ({
    title: isFr ? 'Envoyer de l argent' : 'Send Money',
    subtitleDiaspora: isFr ? 'Payez depuis le solde du portefeuille, puis livrez vers un autre portefeuille MojoPayr, mobile money ou un compte bancaire dans le marche de destination.' : 'Pay from wallet balance, then deliver to another MojoPayr wallet, mobile money, or a bank in the destination market.',
    subtitleLocal: isFr ? 'Utilisez le meme parcours avec solde portefeuille apres recharge locale par mobile money, puis envoyez vers un autre portefeuille, mobile money ou paiement bancaire.' : 'Use the same wallet-backed flow after local wallet funding through mobile money, then move value to another wallet, mobile money, or bank payout.',
    diasporaSender: isFr ? 'Expediteur diaspora' : 'Diaspora sender',
    localSender: isFr ? 'Expediteur local' : 'Local sender',
    recipientLookup: isFr ? 'Verification du destinataire MojoPayr' : 'Real MojoPayr recipient lookup',
    payoutRail: isFr ? 'Paiement depuis le portefeuille' : 'Wallet-backed payout rail',
    deliveryRoute: isFr ? 'Mode de livraison' : 'Delivery route',
    paymentSource: isFr ? 'Source de paiement' : 'Payment source',
    recipient: isFr ? 'Email ou telephone du destinataire' : 'Recipient email or phone',
    receivingCountry: isFr ? 'Pays de reception' : 'Receiving Country',
    lookingUp: isFr ? 'Verification du portefeuille destinataire...' : 'Looking up recipient wallet...',
    fundsCredit: isFr ? 'Les fonds seront credites sur le portefeuille' : 'Funds will credit the',
    wallet: isFr ? 'portefeuille' : 'wallet',
    mobileNumber: isFr ? 'Numero mobile' : 'Mobile Number',
    chooseCountry: isFr ? 'Choisissez d abord le pays de reception.' : 'Choose the receiving country first.',
    bankName: isFr ? 'Nom de la banque' : 'Bank name',
    accountNumber: isFr ? 'Numero de compte' : 'Account number',
    receiverName: isFr ? 'Nom du beneficiaire' : 'Receiver Name',
    processing: isFr ? 'Traitement du transfert...' : 'Processing transfer...',
    confirm: isFr ? 'Confirmer l envoi' : 'Confirm Send',
    summary: isFr ? 'Resume' : 'Summary',
    delivery: isFr ? 'Livraison' : 'Delivery',
    youSend: isFr ? 'Vous envoyez' : 'You Send',
    fees: isFr ? 'Frais' : 'Fees',
    total: isFr ? 'Total a payer' : 'Total You Pay',
    receive: isFr ? 'Ils recoivent' : 'They Receive',
    receipt: isFr ? 'Recu du transfert' : 'Transfer Receipt',
    sent: isFr ? 'Argent envoye avec succes.' : 'Money sent successfully.',
    receiver: isFr ? 'Beneficiaire' : 'Receiver',
    destination: isFr ? 'Destination' : 'Destination',
    mobile: isFr ? 'Mobile' : 'Mobile',
    bank: isFr ? 'Banque' : 'Bank',
    paid: isFr ? 'Total paye' : 'Total Paid',
    txid: isFr ? 'ID de transaction' : 'Transaction ID',
    done: isFr ? 'Termine' : 'Done',
})
const SendMoney = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { lang } = useLanguage()
    const wallet = useWalletSafe()
    const isFr = lang === 'fr'
    const text = sendText(isFr)
    const countryCode = String(user?.country || 'US').toUpperCase()
    const isDiaspora = diasporaCountryCodes.includes(countryCode)
    const senderCurrency = getSenderCurrency(user)
    const [deliveryMethod, setDeliveryMethod] = useState('wallet')
    const [paymentMethod, setPaymentMethod] = useState('wallet')
    const [receivingCountry, setReceivingCountry] = useState('')
    const [recipientIdentifier, setRecipientIdentifier] = useState('')
    const [recipientLookup, setRecipientLookup] = useState(null)
    const [lookupLoading, setLookupLoading] = useState(false)
    const [lookupError, setLookupError] = useState('')
    const [mobileNumber, setMobileNumber] = useState('')
    const [receiverName, setReceiverName] = useState('')
    const [bankName, setBankName] = useState('')
    const [bankAccount, setBankAccount] = useState('')
    const [amountInput, setAmountInput] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [doneOpen, setDoneOpen] = useState(false)
    const [receipt, setReceipt] = useState(null)

    const countryRule = receivingCountry ? sendCountries[receivingCountry] : null
    const availableBalance = Number(wallet?.getBalance?.(senderCurrency) || 0)
    const phoneError = deliveryMethod === 'mobile' ? validatePhone(mobileNumber, countryRule) : ''
    const normalizedMobile = countryRule ? `${countryRule.dialCode}${buildLocalPhone(mobileNumber, countryRule)}` : ''
    const parsedInput = Number(amountInput || 0)
    const walletTargetCurrency = recipientLookup?.wallet_currency || senderCurrency

    useEffect(() => {
        setPaymentMethod('wallet')
    }, [])

    useEffect(() => {
        if (deliveryMethod !== 'wallet') {
            setRecipientLookup(null)
            setLookupError('')
            return undefined
        }
        const identifier = recipientIdentifier.trim()
        if (identifier.length < 3) {
            setRecipientLookup(null)
            setLookupError('')
            return undefined
        }
        const timer = setTimeout(async () => {
            setLookupLoading(true)
            setLookupError('')
            try {
                const matched = ((await api.get('/transfers/recipient-lookup', { params: { identifier } }))?.data?.recipient || null)
                setRecipientLookup(matched)
                if (matched?.name) setReceiverName(matched.name)
            } catch (error) {
                setRecipientLookup(null)
                setLookupError(firstValidationMessage(error) || (error instanceof Error ? error.message : 'Recipient lookup failed.'))
            } finally {
                setLookupLoading(false)
            }
        }, 350)
        return () => clearTimeout(timer)
    }, [deliveryMethod, recipientIdentifier])

    const pricing = useMemo(() => {
        if (parsedInput <= 0) {
            return { senderAmount: 0, fee: 0, totalDebit: 0, receiverAmount: 0, exchangeRateText: isFr ? 'Entrez un montant pour voir le resultat du transfert.' : 'Enter an amount to see the transfer outcome.', pricingNote: deliveryMethod === 'wallet' ? (isFr ? 'Les envois portefeuille a portefeuille peuvent arriver dans des portefeuilles diaspora ou locaux.' : 'Wallet to wallet sends can land in diaspora wallets or local market wallets.') : (isFr ? 'Choisissez une route de paiement et entrez un montant pour continuer.' : 'Choose a payout route and enter an amount to continue.') }
        }
        if (deliveryMethod === 'wallet') {
            const senderAmount = parsedInput
            const fee = isDiaspora ? senderAmount * 0.015 + 0.75 : senderAmount * 0.004
            const receiverAmount = senderAmount * getExchangeRate(senderCurrency, walletTargetCurrency)
            return {
                senderAmount,
                fee,
                totalDebit: senderAmount + fee,
                receiverAmount,
                exchangeRateText: senderCurrency === walletTargetCurrency ? `Sender and receiver stay in ${senderCurrency}.` : `1 ${senderCurrency} = ${getExchangeRate(senderCurrency, walletTargetCurrency).toFixed(2)} ${walletTargetCurrency}`,
                pricingNote: isDiaspora ? (isFr ? 'Les envois diaspora debitent le solde du portefeuille et creditent le portefeuille MojoPayr du destinataire dans la devise du marche.' : 'Diaspora wallet sends debit the resident wallet balance and credit the receiving MojoPayr wallet in the recipient market currency.') : (isFr ? 'Les envois locaux restent simples et peuvent aller directement vers un autre portefeuille MojoPayr.' : 'Local wallet sends stay light and can move directly to another MojoPayr wallet without cashing out first.'),
            }
        }
        if (!countryRule) {
            return { senderAmount: 0, fee: 0, totalDebit: 0, receiverAmount: 0, exchangeRateText: text.chooseCountry, pricingNote: isFr ? 'Les prix selon le pays apparaitront ici.' : 'Country-based payout pricing will appear here.' }
        }
        const senderToUsd = 1 / (usdToCurrency[senderCurrency] || 1)
        if (isDiaspora) {
            const senderAmount = parsedInput
            const fee = senderAmount * 0.015 + 0.75
            const receiverAmount = senderAmount * senderToUsd * countryRule.senderRateFromUsd
            return {
                senderAmount,
                fee,
                totalDebit: senderAmount + fee,
                receiverAmount,
                exchangeRateText: `1 ${senderCurrency} = ${(senderToUsd * countryRule.senderRateFromUsd).toFixed(2)} ${countryRule.receiveCurrency}`,
                pricingNote: deliveryMethod === 'bank' ? (isFr ? 'Les envois bancaires diaspora arrivent dans le marche de reception avec la meme logique de corridor.' : 'Diaspora bank sends settle into the receiving market using the same corridor logic as your standard remittance flow.') : (isFr ? 'Les envois mobile money diaspora suivent la meme logique de corridor et de change.' : 'Diaspora mobile money sends follow the same corridor logic and FX path used across your payout network.'),
            }
        }
        const receiverAmount = parsedInput
        const fee = receiverAmount * 0.004
        return {
            senderAmount: receiverAmount,
            fee,
            totalDebit: receiverAmount + fee,
            receiverAmount,
            exchangeRateText: `Local send in ${countryRule.receiveCurrency}. No cross-border conversion needed.`,
            pricingNote: deliveryMethod === 'bank' ? (isFr ? 'Les paiements bancaires locaux depuis le portefeuille utilisent des frais locaux reduits avant reglement.' : 'Local wallet-funded bank payouts use a lighter local fee path before settlement.') : (isFr ? 'Les envois mobile money locaux restent sur le meme parcours depuis le portefeuille.' : 'Local mobile money sends stay on the same wallet-backed path after the sender funds their wallet.'),
        }
    }, [countryRule, deliveryMethod, isDiaspora, isFr, parsedInput, senderCurrency, text.chooseCountry, walletTargetCurrency])

    const canContinue = useMemo(() => {
        if (submitting || parsedInput <= 0 || !receiverName.trim()) return false
        if (deliveryMethod === 'wallet') return !!recipientLookup && !lookupLoading
        if (deliveryMethod === 'mobile') return !!countryRule && !phoneError
        return !!countryRule && !!bankName.trim() && !!bankAccount.trim()
    }, [bankAccount, bankName, countryRule, deliveryMethod, lookupLoading, parsedInput, phoneError, recipientLookup, receiverName, submitting])

    const handleSubmit = async () => {
        if (!canContinue) return
        setSubmitting(true)
        setSubmitError('')
        if (availableBalance < pricing.totalDebit) {
            setSubmitError('Your wallet balance is not enough. Please add money to continue.')
            setSubmitting(false)
            return
        }
        try {
            const payload = {
                pay_with: paymentMethod,
                send_from: senderCurrency,
                send_to: deliveryMethod === 'wallet' ? recipientLookup?.country || '' : receivingCountry,
                amount: Number(pricing.senderAmount.toFixed(2)),
                fee: Number(pricing.fee.toFixed(2)),
                receiver_amount: Number(pricing.receiverAmount.toFixed(2)),
                receiver_currency: deliveryMethod === 'wallet' ? walletTargetCurrency : countryRule?.receiveCurrency || senderCurrency,
                deliver_to: deliveryMethod,
                recipient_name: receiverName.trim(),
            }
            if (deliveryMethod === 'wallet') payload.recipient_identifier = recipientIdentifier.trim()
            if (deliveryMethod === 'mobile') payload.recipient_phone = normalizedMobile
            if (deliveryMethod === 'bank') {
                payload.bank_name = bankName.trim()
                payload.bank_account = bankAccount.trim()
                if (mobileNumber.trim()) payload.recipient_phone = mobileNumber.trim()
            }
            const response = ((await api.post('/transfers/send', payload))?.data || {})
            setReceipt({
                ref: response.ref,
                receiverName: receiverName.trim(),
                recipientCountry: deliveryMethod === 'wallet' ? recipientLookup?.country : receivingCountry,
                mobile: deliveryMethod === 'mobile' ? normalizedMobile : '',
                bankName: deliveryMethod === 'bank' ? bankName.trim() : '',
                bankAccount: deliveryMethod === 'bank' ? bankAccount.trim() : '',
                totalDebit: pricing.totalDebit,
                receiverAmount: response.receiver_amount ?? pricing.receiverAmount,
                receiverCurrency: response.receiver_currency || (deliveryMethod === 'wallet' ? walletTargetCurrency : countryRule?.receiveCurrency || senderCurrency),
            })
            setDoneOpen(true)
        } catch (error) {
            setSubmitError(firstValidationMessage(error))
        } finally {
            setSubmitting(false)
        }
    }

    const amountLabel = deliveryMethod === 'wallet' ? `${text.youSend} (${senderCurrency})` : isDiaspora ? `${text.youSend} (${senderCurrency})` : `${text.receive} (${countryRule?.receiveCurrency || senderCurrency})`
    const deliveryLabel = (option) => {
        if (option.value === 'wallet') return 'MOJOPAYR WALLET'
        if (option.value === 'mobile') return isFr ? 'MOBILE MONEY' : 'MOBILE MONEY'
        return isFr ? 'COMPTE BANCAIRE' : 'BANK ACCOUNT'
    }

    return (
        <Box sx={{ minHeight: '100vh', py: { xs: 3, md: 4 } }}>
            <Container maxWidth="lg">
                <Stack spacing={3}>
                    <Box>
                        <Typography sx={{ color: '#fff', fontSize: { xs: 32, md: 44 }, fontWeight: 900, letterSpacing: '-0.03em' }}>{text.title}</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.66)', mt: 1, maxWidth: 860 }}>
                            {isDiaspora ? text.subtitleDiaspora : text.subtitleLocal}
                        </Typography>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <Card sx={{ borderRadius: 5, background: 'rgba(26,18,54,0.96)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                    <Stack spacing={2.5}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                            <Chip icon={isDiaspora ? <PublicIcon /> : <PlaceIcon />} label={isDiaspora ? text.diasporaSender : text.localSender} sx={{ color: '#FFF', bgcolor: 'rgba(255,138,0,0.14)' }} />
                                            <Chip icon={<VerifiedIcon />} label={deliveryMethod === 'wallet' ? text.recipientLookup : text.payoutRail} sx={{ color: '#FFF', bgcolor: 'rgba(91,46,255,0.22)' }} />
                                        </Stack>
                                        <Typography sx={{ color: '#fff', fontWeight: 800 }}>{text.deliveryRoute}</Typography>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            {deliveryOptions.map((option) => (
                                                <Button key={option.value} onClick={() => { setDeliveryMethod(option.value); setSubmitError('') }} startIcon={option.icon} sx={{ flex: 1, borderRadius: 3, py: 1.4, color: '#fff', border: '1px solid rgba(255,255,255,0.08)', background: deliveryMethod === option.value ? 'linear-gradient(90deg,#FF8A00,#FFB347)' : 'rgba(255,255,255,0.04)' }}>{deliveryLabel(option)}</Button>
                                            ))}
                                        </Stack>
                                        <Typography sx={{ color: '#fff', fontWeight: 800 }}>{text.paymentSource}</Typography>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            <Button onClick={() => setPaymentMethod('wallet')} startIcon={<WalletIcon />} sx={{ flex: 1, borderRadius: 3, py: 1.2, color: '#fff', border: '1px solid rgba(255,255,255,0.08)', background: paymentMethod === 'wallet' ? 'rgba(255,138,0,0.18)' : 'rgba(255,255,255,0.04)' }}>WALLET BALANCE</Button>
                                        </Stack>
                                        {deliveryMethod === 'wallet' ? <TextField label={text.recipient} value={recipientIdentifier} onChange={(e) => setRecipientIdentifier(e.target.value)} fullWidth sx={inputSx} /> : <TextField select label={text.receivingCountry} value={receivingCountry} onChange={(e) => setReceivingCountry(e.target.value)} fullWidth sx={inputSx}><MenuItem value="" disabled>{text.receivingCountry}</MenuItem>{Object.values(sendCountries).map((country) => <MenuItem key={country.code} value={country.code}>{country.name} ({country.receiveCurrency})</MenuItem>)}</TextField>}
                                        {lookupLoading && deliveryMethod === 'wallet' && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} sx={{ color: '#FFB347' }} /><Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>{text.lookingUp}</Typography></Stack>}
                                        {recipientLookup && deliveryMethod === 'wallet' && <Alert severity="success" sx={{ borderRadius: 3, bgcolor: 'rgba(34,197,94,0.12)', color: '#fff' }}>{recipientLookup.name} matched in {recipientLookup.country}. {text.fundsCredit} {recipientLookup.wallet_currency} {text.wallet}.</Alert>}
                                        {lookupError && deliveryMethod === 'wallet' && !lookupLoading && <Alert severity="error" sx={{ borderRadius: 3, bgcolor: 'rgba(248,113,113,0.12)', color: '#fff' }}>{lookupError}</Alert>}
                                        {deliveryMethod === 'mobile' && <TextField label={text.mobileNumber} value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} fullWidth sx={inputSx} disabled={!countryRule} helperText={countryRule ? `${countryRule.name}: use local or ${countryRule.dialCode} format` : text.chooseCountry} FormHelperTextProps={{ sx: { color: phoneError ? '#FF8F8F' : 'rgba(255,255,255,0.48)' } }} error={!!phoneError && !!mobileNumber} />}
                                        {deliveryMethod === 'bank' && <Grid container spacing={2}><Grid item xs={12} sm={6}><TextField label={text.bankName} value={bankName} onChange={(e) => setBankName(e.target.value)} fullWidth sx={inputSx} disabled={!countryRule} /></Grid><Grid item xs={12} sm={6}><TextField label={text.accountNumber} value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} fullWidth sx={inputSx} disabled={!countryRule} /></Grid></Grid>}
                                        <TextField label={text.receiverName} value={receiverName} onChange={(e) => setReceiverName(e.target.value)} fullWidth sx={inputSx} />
                                        <TextField label={amountLabel} value={amountInput} onChange={(e) => setAmountInput(e.target.value)} fullWidth sx={inputSx} />
                                        {submitError && <Alert severity="error" sx={{ borderRadius: 3, bgcolor: 'rgba(248,113,113,0.12)', color: '#fff' }}>{submitError}</Alert>}
                                        <Button fullWidth startIcon={submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon />} onClick={handleSubmit} disabled={!canContinue} sx={{ mt: 1, py: 1.7, borderRadius: 4, background: 'linear-gradient(90deg,#FF8A00,#FFB347)', color: '#ffffff', fontWeight: 800, textTransform: 'none', '&:hover': { background: 'linear-gradient(90deg,#FF941F,#FFC46B)' } }}>{submitting ? text.processing : text.confirm}</Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Stack spacing={2.5}>
                                <Card sx={{ borderRadius: 5, background: 'rgba(26,18,54,0.96)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>{text.summary}</Typography>
                                        <Divider sx={{ my: 2, opacity: 0.14, borderColor: '#fff' }} />
                                        <Stack spacing={1.6}>
                                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72, color: '#fff' }}>{text.delivery}</Typography><Typography sx={{ color: '#fff', fontWeight: 700 }}>{deliveryLabel(deliveryOptions.find((option) => option.value === deliveryMethod) || deliveryOptions[0])}</Typography></Stack>
                                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72, color: '#fff' }}>{text.youSend}</Typography><Typography sx={{ color: '#fff', fontWeight: 700 }}>{formatMoney(pricing.senderAmount, senderCurrency)}</Typography></Stack>
                                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72, color: '#fff' }}>{text.fees}</Typography><Typography sx={{ color: '#fff', fontWeight: 700 }}>{formatMoney(pricing.fee, senderCurrency)}</Typography></Stack>
                                            <Divider sx={{ opacity: 0.14, borderColor: '#fff' }} />
                                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72, color: '#fff' }}>{text.total}</Typography><Typography sx={{ color: '#FFB347', fontWeight: 900 }}>{formatMoney(pricing.totalDebit, senderCurrency)}</Typography></Stack>
                                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72, color: '#fff' }}>{text.receive}</Typography><Typography sx={{ color: '#FF8A00', fontWeight: 900 }}>{formatMoney(pricing.receiverAmount, deliveryMethod === 'wallet' ? walletTargetCurrency : countryRule?.receiveCurrency || senderCurrency)}</Typography></Stack>
                                            <Typography sx={{ fontSize: 12, opacity: 0.6, color: '#fff' }}>{pricing.exchangeRateText}</Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>{pricing.pricingNote}</Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
                <Dialog open={doneOpen} fullWidth maxWidth="sm" PaperProps={{ sx: { backgroundColor: '#1A1236', borderRadius: 4, border: '1px solid rgba(255,138,0,0.25)', color: '#ffffff' } }}>
                    <DialogTitle sx={{ fontWeight: 800 }}>{text.receipt}</DialogTitle>
                    <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                        <Stack spacing={2}>
                            <Alert severity="success" sx={{ borderRadius: 3, bgcolor: 'rgba(255,138,0,0.12)', color: '#fff' }}>{text.sent}</Alert>
                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72 }}>{text.receiver}</Typography><Typography fontWeight={700}>{receipt?.receiverName}</Typography></Stack>
                            {receipt?.recipientCountry && <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72 }}>{text.destination}</Typography><Typography fontWeight={700}>{receipt?.recipientCountry}</Typography></Stack>}
                            {receipt?.mobile && <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72 }}>{text.mobile}</Typography><Typography fontWeight={700}>{receipt?.mobile}</Typography></Stack>}
                            {receipt?.bankName && <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72 }}>{text.bank}</Typography><Typography fontWeight={700}>{receipt?.bankName} / {receipt?.bankAccount}</Typography></Stack>}
                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72 }}>{text.paid}</Typography><Typography sx={{ color: '#FFB347', fontWeight: 900 }}>{formatMoney(receipt?.totalDebit || 0, senderCurrency)}</Typography></Stack>
                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72 }}>{text.receive}</Typography><Typography sx={{ color: '#FF8A00', fontWeight: 900 }}>{formatMoney(receipt?.receiverAmount || 0, receipt?.receiverCurrency || senderCurrency)}</Typography></Stack>
                            <Divider sx={{ opacity: 0.2, borderColor: '#fff' }} />
                            <Stack direction="row" justifyContent="space-between"><Typography sx={{ opacity: 0.72 }}>{text.txid}</Typography><Typography fontWeight={800}>{receipt?.ref}</Typography></Stack>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button fullWidth variant="contained" sx={{ backgroundColor: '#FF8A00', color: '#ffffff', fontWeight: 800, borderRadius: 3 }} onClick={() => { setDoneOpen(false); navigate('/dashboard') }}>{text.done}</Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    )
}

export default SendMoney
